import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { 
  FileText, Globe, Languages, Sparkles, Check, Loader2, ChevronDown, X
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * SEOPanel Component
 * Displays SEO add-on status, features, and blog generation
 */
export default function SEOPanel() {
  const [loading, setLoading] = useState(true);
  const [seoStatus, setSeoStatus] = useState(null);
  const [enabling, setEnabling] = useState(false);
  const [disabling, setDisabling] = useState(false);
  const [blogs, setBlogs] = useState([]);

  useEffect(() => {
    fetchSEOStatus();
    fetchBlogs();
  }, []);

  const fetchSEOStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/seo/subscription-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSeoStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch SEO status:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBlogs = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/seo/blogs?limit=5`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBlogs(response.data.blogs || []);
    } catch (error) {
      // SEO might not be enabled
    }
  };

  const enableSEO = async () => {
    setEnabling(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/seo/enable`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('SEO Add-on enabled!');
      fetchSEOStatus();
    } catch (error) {
      toast.error('Failed to enable SEO add-on');
    } finally {
      setEnabling(false);
    }
  };

  const disableSEO = async () => {
    setDisabling(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/seo/disable`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('SEO Add-on disabled');
      fetchSEOStatus();
    } catch (error) {
      toast.error('Failed to disable SEO add-on');
    } finally {
      setDisabling(false);
    }
  };

  if (loading) {
    return (
      <Card className="glass-card border-white/5 animate-pulse">
        <CardContent className="p-6">
          <div className="h-32 bg-white/5 rounded" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="glass-card border-white/5">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <FileText className="w-5 h-5 text-primary" />
            SEO Blog Generator
            {seoStatus?.seo_enabled && (
              <Badge className="bg-green-500/20 text-green-400 border-green-500/30 text-xs">
                Active
              </Badge>
            )}
          </CardTitle>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-xs">
              {seoStatus?.price || '$200/month'}
            </Badge>
            {seoStatus?.seo_enabled && (
              <Button
                variant="ghost"
                size="sm"
                onClick={disableSEO}
                disabled={disabling}
                className="h-7 px-2 text-xs text-red-400 hover:text-red-300 hover:bg-red-500/10"
                data-testid="disable-seo-btn"
              >
                {disabling ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <>
                    <X className="w-3 h-3 mr-1" />
                    Disable
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {!seoStatus?.seo_enabled ? (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Convert your social media content into SEO-optimized blog posts automatically.
            </p>
            <div className="grid grid-cols-2 gap-2 text-xs">
              {seoStatus?.features?.slice(0, 4).map((feature, i) => (
                <div key={i} className="flex items-center gap-1.5 text-muted-foreground">
                  <Check className="w-3 h-3 text-green-500" />
                  <span>{feature}</span>
                </div>
              ))}
            </div>
            <Button 
              onClick={enableSEO} 
              disabled={enabling}
              className="w-full"
              data-testid="enable-seo-btn"
            >
              {enabling ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Enabling...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Enable SEO Add-on
                </>
              )}
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">
                {blogs.length} blogs generated
              </span>
              <Button variant="outline" size="sm" data-testid="generate-blog-btn">
                <Sparkles className="w-3 h-3 mr-1" />
                Generate Blog
              </Button>
            </div>
            
            {blogs.length > 0 ? (
              <div className="space-y-2">
                {blogs.slice(0, 3).map((blog) => (
                  <div 
                    key={blog.id}
                    className="p-2 rounded bg-white/5 border border-white/10 hover:border-white/20 transition-colors cursor-pointer"
                  >
                    <p className="text-sm font-medium truncate">{blog.title}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge variant="outline" className="text-xs">
                        {blog.language || 'en'}
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        {blog.word_count || 0} words
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-4 text-sm text-muted-foreground">
                <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
                No blogs generated yet. Create a campaign first!
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
